import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Form, Divider, List, Button, Message } from "semantic-ui-react";
import { Promotions, Rates } from '../../../api/services/next'
import { useMessageResources, useTextResources } from '../../../language';
import useBoolean from "../../../hooks/useBoolean";
import useSafeState from "../../../hooks/useSafeState";
import { useDateTimeRange } from '../../../hooks/useDateTimeRange';
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { onError, onWarning, onSuccess } from '../../../components/shared/messages';
import TextInput from "../../../components/shared/controlled/TextInput";
import HotelsDropDown from "../../../components/shared/HotelsDropDown";
import RatesDropDown from '../../../components/shared/RatesDropDown';
import WeekDaysSelect from '../../../components/shared/WeekDaysSelect';
import NumericInput from '../../../components/shared/NumericInput';
import { DatePicker } from '../../../components/shared/DatePicker';
import { formatDateRange, validateDateRange, validateDateRangeMessage, rearangeDateRanges } from "../../../components/shared/utils";
import Weekdays from '../../../components/shared/Weekdays';
import Panel from '../../../components/shared/Panel';
import { TrashButton } from '../../../components/shared/icons';
import { RateDiscountTypesEnum, DiscountApplicationTypesEnum, Designations } from '../../../components/shared/constants';
import { useLoader } from "../../../hooks/useLoader";

const defaultState = {
    Id: 0,
    Name: '',
    RateDiscountTypeId: RateDiscountTypesEnum.HomeSiteDiscount,
    DiscountApplicationTypeId: DiscountApplicationTypesEnum.Product,
    RateDiscountAssignments: [],
    ChildRateDiscounts: [],
};

const generateFakeId = () => {
    return `childDiscount-${crypto.randomUUID()}`;
};

const isRealId = (id) => {
    return typeof id === 'number' && id > 0;
};

const EditHomeSitePromotionPopup = ({
    open,
    homeSitePromotionId,
    onSave,
    onClose,
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();

    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);

    const isCreate = !homeSitePromotionId;
    const [homeSitePromotionState, setHomeSitePromotionState] = useSafeState(defaultState);

    const [selectedHotel, setSelectedHotel] = useState(null);
    const [ratesData, setRatesData] = useSafeState([]);

    const { startDate, endDate, setStartDate, setEndDate, reset: resetStartEndDate } = useDateTimeRange({
        clearable: false,
    });
    const [selectedDays, setSelectedDays] = useState([]);
    const [selectedRates, setSelectedRates] = useState([]);
    const loadHomeSitePromotion = useCallback(async () => {
        try {
            loader(true);
            const promotion = await Promotions.GetHomeSitePromotion({ id: homeSitePromotionId });
            setHomeSitePromotionState(promotion);
            setSelectedHotel(promotion.Rates[0]?.HotelId);
        }
        catch (e) {
            onError(e);
            setHomeSitePromotionState(defaultState);
        }
        finally {
            loader(false);
        }
    }, [homeSitePromotionId, loader, setHomeSitePromotionState]);

    useEffect(() => {
        if (homeSitePromotionId > 0) {
            loadHomeSitePromotion();
        } else {
            setHomeSitePromotionState(defaultState);
        }
        return () => {
            setHomeSitePromotionState(defaultState);
            resetStartEndDate();
        };
    }, [homeSitePromotionId, loadHomeSitePromotion, resetStartEndDate, setHomeSitePromotionState]);

    const loadHotelRates = useCallback(async () => {
        if (selectedHotel) {
            loader(true);
            const rates = await Rates.GetRates({
                HotelId: selectedHotel,
                InheritedRates: true,
            });
            setRatesData(rates);
            setSelectedRates(homeSitePromotionState?.RateDiscountAssignments?.map((x) => x.RateId));
            loader(false);
        }
    }, [loader, selectedHotel, setRatesData]);

    useEffect(() => {
        if (selectedHotel) {
            loadHotelRates();
        }
    }, [loadHotelRates, selectedHotel])

    const handleRatesChanged = useCallback((rateIds) => {
        setHomeSitePromotionState((prevState) => ({
            ...prevState,
            RateDiscountAssignments: rateIds.map((rateId) => ({ DiscountId: homeSitePromotionId, RateId: rateId })),
        }));
    }, [homeSitePromotionId, setHomeSitePromotionState]);

    const setName = useCallback((value) => {
        setHomeSitePromotionState((prevState) => ({
            ...prevState,
            Name: value,
        }));
    }, [setHomeSitePromotionState]);

    const handleAddChildDiscount = useCallback(() => {
        const newChildDiscount = {
            Id: generateFakeId(),
            StartDate: startDate,
            EndDate: endDate,
            Weekdays: selectedDays,
            RateDiscountTypeId: RateDiscountTypesEnum.HomeSiteDiscount,
            DiscountApplicationTypeId: DiscountApplicationTypesEnum.RoomType,
            ParentDiscountId: homeSitePromotionId,
            RateDiscountAmounts: [{
                Id: 0,
                RateDiscountId: 0,
                DesignationId: Designations.Overall,
                DiscountPercent: 0,
            }],
        };

        const options = {
            getRange: e => ({ range: [e.StartDate, e.EndDate], weekdays: e.Weekdays }),
            setRange: (e, range) => {
                e.StartDate = range[0];
                e.EndDate = range[1];
                e.Weekdays = range.weekdays || e.Weekdays;
            },
            deleteElement: e => { e.IsActive = false; },
            predicate: (e) => (e.IsActive != false && e.StartDate && e.EndDate),
            copyElement: (e, startDate, endDate) => ({
                ...e,
                Id: 0,
                StartDate: startDate,
                EndDate: endDate,
                Weekdays: e.Weekdays || 0
            })
        }
        const updatedDiscounts = rearangeDateRanges(newChildDiscount, homeSitePromotionState.ChildRateDiscounts, options)

        setHomeSitePromotionState((prevState) => {
            return {
                ...prevState,
                ChildRateDiscounts: updatedDiscounts.filter(e => e.IsActive !== false)
            };
        });
        resetStartEndDate();
    }, [endDate, homeSitePromotionId, homeSitePromotionState.ChildRateDiscounts, resetStartEndDate, selectedDays, setHomeSitePromotionState, startDate]);

    const handleRemoveChildDiscount = useCallback((id) => {
        setHomeSitePromotionState((prevState) => ({
            ...prevState,
            ChildRateDiscounts: prevState.ChildRateDiscounts.filter((childDiscount) => childDiscount.Id !== id),
        }));
    }, [setHomeSitePromotionState]);

    const handleEditChildDiscount = useCallback((newChildDiscount) => {
        setHomeSitePromotionState((prevState) => ({
            ...prevState,
            ChildRateDiscounts: prevState.ChildRateDiscounts.map(
                (childDiscount) => childDiscount.Id === newChildDiscount.Id ? newChildDiscount : childDiscount),
        }));
    }, [setHomeSitePromotionState]);

    const isValid = useCallback((homeSitePromotionToCheck) => {
        if (!homeSitePromotionToCheck.Name) {
            onWarning(message('NameIsMandatoryField'));
            return false;
        }

        if (!homeSitePromotionToCheck.RateDiscountAssignments.length) {
            onWarning(message('RateIsRequired'));
            return false;
        }

        if (!homeSitePromotionToCheck.ChildRateDiscounts.length) {
            onWarning(message('DateRangesSelectionIsMandatory'));
            return false;
        }

        if (homeSitePromotionToCheck.ChildRateDiscounts.some((childDiscount) => !childDiscount.RateDiscountAmounts[0].DiscountPercent)) {
            onWarning(message('RateValidationDiscountPercentEmpty'));
            return false;
        }

        return true;
    }, [message]);

    const getPromotionToSave = useCallback((homeSitePromotionToSave) => {
        return {
            ...homeSitePromotionToSave,
            ChildRateDiscounts: homeSitePromotionToSave.ChildRateDiscounts.map(childDiscount => ({
                ...childDiscount,
                Id: isRealId(childDiscount.Id) ? childDiscount.Id : 0,
            }))
        };
    }, []);

    const handleSave = useCallback(async () => {
        if (!isValid(homeSitePromotionState)) {
            return;
        }

        try {
            setLoading();
            const promotionToSave = getPromotionToSave(homeSitePromotionState);
            await Promotions.CreateOrUpdateHomeSitePromotion(promotionToSave);
            await onSave();
            onSuccess();
        } catch (e) {
            onError(e);
            return false;
        } finally {
            resetLoading();
        }

        return true;
    }, [getPromotionToSave, homeSitePromotionState, isValid, onSave, resetLoading, setLoading]);

    const handleSaveAndCreateNew = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && setHomeSitePromotionState(defaultState);
        return isSaved;
    }, [handleSave, setHomeSitePromotionState]);

    const handleClose = useCallback(() => {
        setHomeSitePromotionState(defaultState);
        setRatesData([]);
        setSelectedRates([]);
        setSelectedHotel(null);
        onClose();
    }, [onClose, setHomeSitePromotionState]);

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && handleClose();
    }, [handleSave, handleClose]);

    const memoDatesForWeekComponent = useMemo(() => {
        return startDate && endDate ? [{ startDate, endDate }] : []
    }, [startDate, endDate]);

    if (!isCreate && !homeSitePromotionState.Id) {
        return null;
    }

    return (
        <EditEntityPopup
            open={open}
            isCreate={isCreate}
            header={`${isCreate ? text("Create") : text("Edit")} ${text("HomeSiteDiscount")}`}
            onClose={handleClose}
            onSave={isCreate ? handleSaveAndCreateNew : undefined}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
            isValidDateRange={validateDateRange(startDate, endDate, selectedDays)}
        >
            <Form warning={!validateDateRange(startDate, endDate, selectedDays)}>
                <Form.Group className='home-site-promotion-rates'>
                    <Form.Field
                        control={TextInput}
                        label={text('Name')}
                        value={homeSitePromotionState.Name}
                        onChange={setName}
                        width={4}
                        maxLength={100}
                    />
                    <Form.Field
                        label={text('Hotel')}
                        control={HotelsDropDown}
                        value={selectedHotel}
                        onChange={setSelectedHotel}
                        search
                        width={4}
                    />
                    <Form.Field
                        label={text('Rates')}
                        control={RatesDropDown}
                        data={ratesData}
                        value={selectedRates}
                        onChange={handleRatesChanged}
                        multiple
                        clearable
                        search
                        width={6}
                    />
                </Form.Group>
                <Divider style={{ opacity: 0.5 }} />
                <Form.Group className='home-site-promotion-dates' width={12}>
                    <Form.Field
                        control={DatePicker}
                        unstackable
                        label={text('From')}
                        onChange={setStartDate}
                        clearable={false}
                        value={startDate}
                        width={4}
                    />
                    <Form.Field
                        control={DatePicker}
                        unstackable
                        label={text('To')}
                        onChange={setEndDate}
                        clearable={false}
                        value={endDate}
                        width={4}
                    />
                    <Form.Field
                        control={WeekDaysSelect}
                        onChange={setSelectedDays}
                        dates={memoDatesForWeekComponent}
                        initDays={isCreate ? [] : selectedDays}
                        width={6}
                    />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            primary
                            onClick={handleAddChildDiscount}
                        >
                            {text("Add")}
                        </Button>
                    </div>
                </Form.Group>
                <Message
                    warning
                    content={text(validateDateRangeMessage(startDate, endDate, selectedDays))}
                />
                {homeSitePromotionState.ChildRateDiscounts.length > 0 && (
                    <>
                        <Divider style={{ opacity: 0.5 }} />
                        <List className='home-site-promotion-child-discount'>
                            {homeSitePromotionState.ChildRateDiscounts.map((childDiscount) => (
                                <ChildDiscount
                                    key={childDiscount.Id}
                                    childDiscount={childDiscount}
                                    editItem={handleEditChildDiscount}
                                    removeItem={handleRemoveChildDiscount}
                                />
                            ))}
                        </List>
                    </>
                )}
            </Form>
        </EditEntityPopup>
    );
};

const ChildDiscount = ({
    childDiscount,
    editItem,
    removeItem,
}) => {
    const text = useTextResources();

    const handleDiscountAmountChange = useCallback((value) => {
        const newChildDiscount = {
            ...childDiscount,
            RateDiscountAmounts: [...childDiscount.RateDiscountAmounts.map(rda => ({ ...rda }))],
        };
        newChildDiscount.RateDiscountAmounts[0].DiscountPercent = value;
        editItem(newChildDiscount);
    }, [childDiscount, editItem]);

    const handleRemoveClicked = useCallback(() => {
        removeItem(childDiscount.Id);
    }, [childDiscount.Id, removeItem]);

    return (
        <List.Item key={childDiscount.Id}>
            <Panel
                color={childDiscount?.Id ? "red" : ""}
                header={
                    <List.Description
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: 5,
                                flexDirection: "row"
                            }}
                        >
                            {formatDateRange(childDiscount.StartDate, childDiscount.EndDate)}
                            <Weekdays selectedWeekdaysInt={childDiscount.Weekdays} />
                        </div>
                        <TrashButton onClick={handleRemoveClicked} />
                    </List.Description>
                }
            >
                <div style={{ display: 'flex' }}>
                    <Form.Field
                        label={text('Discount')}
                        buttonPlacement='none'
                        control={NumericInput}
                        value={childDiscount.RateDiscountAmounts[0].DiscountPercent}
                        onChange={handleDiscountAmountChange}
                    />
                </div>
            </Panel>
        </List.Item>
    );
};

ChildDiscount.propTypes = {
    childDiscount: PropTypes.shape({
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        StartDate: PropTypes.instanceOf(Date).isRequired,
        EndDate: PropTypes.instanceOf(Date).isRequired,
        Weekdays: PropTypes.number.isRequired,
        RateDiscountAmounts: PropTypes.arrayOf(PropTypes.shape({
            DiscountPercent: PropTypes.number.isRequired,
        })).isRequired,
    }).isRequired,
    editItem: PropTypes.func.isRequired,
    removeItem: PropTypes.func.isRequired,
};

EditHomeSitePromotionPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    homeSitePromotionId: PropTypes.number,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
};

EditHomeSitePromotionPopup.defaultProps = {
    open: false,
    homeSitePromotionId: 0,
    onClose: () => { },
    onSave: () => Promise.resolve(),
}

export default EditHomeSitePromotionPopup;
